import ExclamationMarkIcon from 'assets/images/icons/ExclamationMarkIcon.svg?react';
import { useInCreateDialogSections } from 'components/create-dialog/CreateDialogRequired';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyFuzzySearchPartiesQuery } from 'store/api/contacts';
import { ClassifierData } from 'types/classifiers';
import { FuzzySearchResultDto, PartyType } from 'types/contacts';
import { formatFullAddress, getContactAddress } from 'utils/addressFormatter';
import { cn } from 'utils/cn';
import { createFuzzySearchHandler, FuzzySearchState, SearchType } from 'utils/fuzzySearchUtils';

interface UseFuzzySearchProps {
    enabled?: boolean;
    partyType?: PartyType;
}

const INITIAL_FUZZY_SEARCH_STATES: Record<SearchType, FuzzySearchState> = {
    NAME: { results: [], isExpanded: false },
    PERSONAL_ID: { results: [], isExpanded: false },
    REGISTRATION_NUMBER: { results: [], isExpanded: false },
};

export const useFuzzySearch = ({ enabled = true, partyType }: UseFuzzySearchProps = {}) => {
    const [fuzzySearchParties] = useLazyFuzzySearchPartiesQuery();
    const [fuzzySearchStates, setFuzzySearchStates] =
        useState<Record<SearchType, FuzzySearchState>>(INITIAL_FUZZY_SEARCH_STATES);
    // Reset the fuzzy-search state when partyType changes by tracking the previous value in render
    // (React-blessed pattern, see https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    // Avoids a useEffect that would set state in an effect.
    const [prevPartyType, setPrevPartyType] = useState(partyType);
    if (partyType !== prevPartyType) {
        setPrevPartyType(partyType);
        setFuzzySearchStates(INITIAL_FUZZY_SEARCH_STATES);
    }

    const updateSearchState = (type: SearchType, update: Partial<FuzzySearchState>) => {
        setFuzzySearchStates((prev) => ({
            ...prev,
            [type]: { ...prev[type], ...update },
        }));
    };

    const searchHandlers = useMemo(() => {
        if (!enabled) return null;

        return {
            name: createFuzzySearchHandler(
                fuzzySearchParties,
                (results) => updateSearchState('NAME', { results }),
                () => updateSearchState('NAME', { isExpanded: false }),
                { searchType: 'NAME', partyTypeFilter: partyType }
            ),
            personalCode: createFuzzySearchHandler(
                fuzzySearchParties,
                (results) => updateSearchState('PERSONAL_ID', { results }),
                () => updateSearchState('PERSONAL_ID', { isExpanded: false }),
                { searchType: 'PERSONAL_ID', partyTypeFilter: PartyType.PERSON }
            ),
            regCode: createFuzzySearchHandler(
                fuzzySearchParties,
                (results) => updateSearchState('REGISTRATION_NUMBER', { results }),
                () => updateSearchState('REGISTRATION_NUMBER', { isExpanded: false }),
                { searchType: 'REGISTRATION_NUMBER', partyTypeFilter: PartyType.COMPANY }
            ),
        };
    }, [enabled, fuzzySearchParties, partyType]);

    useEffect(() => {
        if (!searchHandlers) return;
        return () => {
            searchHandlers.name?.cleanup();
            searchHandlers.personalCode?.cleanup();
            searchHandlers.regCode?.cleanup();
        };
    }, [searchHandlers]);

    return {
        searchHandlers,
        fuzzySearchStates,
        updateSearchState,
    };
};

interface FuzzySearchTableViewProps {
    fuzzySearchResults: FuzzySearchResultDto[];
    partyType: PartyType;
    isAccordionExpanded: boolean;
    setIsAccordionExpanded: (expanded: boolean) => void;
    classifiers: Record<string, ClassifierData>;
}

const CELL_PADDING_CLASS = 'px-4 py-1.5';
/** In the create dialog the panel keeps to the fields' column, which its seven columns fit with less room each. */
const DIALOG_CELL_PADDING_CLASS = 'px-2 py-1.5';

export const FuzzySearchTableView = ({
    fuzzySearchResults,
    partyType,
    isAccordionExpanded,
    setIsAccordionExpanded,
    classifiers,
}: FuzzySearchTableViewProps) => {
    const inCreateDialog = useInCreateDialogSections();

    if (fuzzySearchResults.length === 0) return null;

    const cellPaddingClass = inCreateDialog ? DIALOG_CELL_PADDING_CLASS : CELL_PADDING_CLASS;
    const cellClass = cn(cellPaddingClass, 'whitespace-normal');

    return (
        <Collapsible
            open={isAccordionExpanded}
            onOpenChange={(open) => setIsAccordionExpanded(open)}
            className={cn(
                'border-error-light mt-2 w-fit min-w-full overflow-hidden rounded-lg border-2',
                inCreateDialog && 'max-w-full'
            )}
        >
            <CollapsibleTrigger className="group bg-error-lighter flex min-h-10 w-full cursor-pointer items-center justify-between px-3 py-[5px]">
                <span className="flex items-center gap-2">
                    <ExclamationMarkIcon className="text-error" width={16} />
                    <span className="text-sm font-semibold">
                        <FormattedMessage
                            id="fuzzy-search.potential-duplicates"
                            values={{ count: fuzzySearchResults.length }}
                        />
                    </span>
                </span>
                <ExpandMoreRoundedIcon className="transition-transform group-data-panel-open:rotate-90" />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-auto">
                <Table className="text-xs">
                    <TableHeader>
                        <TableRow>
                            <TableHead className={cellClass}>
                                <FormattedMessage id="name" />
                            </TableHead>
                            <TableHead className={cellClass}>
                                <FormattedMessage id="official-name" />
                            </TableHead>
                            <TableHead className={cellClass}>
                                <FormattedMessage id="contacts.contact-details" />
                            </TableHead>
                            <TableHead className={cellClass}>
                                <FormattedMessage
                                    id={partyType === PartyType.PERSON ? 'contacts.person-id' : 'reg-number'}
                                />
                            </TableHead>
                            <TableHead className={cellClass}>
                                <FormattedMessage id="address" />
                            </TableHead>
                            <TableHead className={cellClass}>
                                <FormattedMessage id="billing-address" />
                            </TableHead>
                            <TableHead className={cellClass}>
                                <FormattedMessage id="fuzzy-search.similarity" />
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {fuzzySearchResults.map((result) => (
                            <TableRow key={result.id}>
                                <TableCell className={cellClass}>
                                    <a
                                        href={`/contact/${result.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-fg hover:text-link no-underline"
                                    >
                                        {result.name}
                                    </a>
                                </TableCell>
                                <TableCell className={cellClass}>{result.officialName || '-'}</TableCell>
                                <TableCell className={cellClass}>
                                    <div>
                                        <div>Email: {result.mainEmail || '-'}</div>
                                        <div>Phone: {result.mainPhone || result.mainMobile || '-'}</div>
                                    </div>
                                </TableCell>
                                <TableCell className={cellPaddingClass}>
                                    {result.partyType === 'PERSON' ? result.personalCode || '-' : result.regCode || '-'}
                                </TableCell>
                                <TableCell className={cellClass}>
                                    {formatFullAddress(getContactAddress(result), classifiers)}
                                </TableCell>
                                <TableCell className={cellClass}>
                                    {formatFullAddress(result.billingAddress, classifiers)}
                                </TableCell>
                                <TableCell className={cellClass}>{(result.similarity * 100).toFixed(1)}%</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CollapsibleContent>
        </Collapsible>
    );
};
